import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin, Tv, Store, Landmark, ChevronRight, ChevronLeft,
  CreditCard, Wallet, CheckCircle2, Loader2, Globe, Building2,
  Link2, Image, Video, Calendar, User, Mail, Phone, Briefcase,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useWallet } from "@/hooks/useWallet";

const BASE = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";

// ── Types ────────────────────────────────────────────────────────────────────

type PlacementType = "sponsored_landmark" | "ad_billboard" | "storefront" | "event_pin";
type LocationType  = "virtual" | "real_world" | "both";
type PaymentMethod = "stripe" | "crypto";

interface Space { id: number; name: string; cityName?: string; region?: string; }

interface FormData {
  placementType:        PlacementType | "";
  locationType:         LocationType  | "";
  virtualSpaceId:       number | null;
  virtualSpaceName:     string;
  virtualLocationDetail:string;
  realWorldAddress:     string;
  realWorldCity:        string;
  title:                string;
  description:          string;
  imageUrl:             string;
  videoUrl:             string;
  externalLinkUrl:      string;
  durationDays:         number;
  contactName:          string;
  contactEmail:         string;
  contactPhone:         string;
  contactCompany:       string;
  paymentMethod:        PaymentMethod | "";
}

const EMPTY: FormData = {
  placementType: "", locationType: "", virtualSpaceId: null,
  virtualSpaceName: "", virtualLocationDetail: "", realWorldAddress: "",
  realWorldCity: "", title: "", description: "", imageUrl: "", videoUrl: "",
  externalLinkUrl: "", durationDays: 30, contactName: "", contactEmail: "",
  contactPhone: "", contactCompany: "", paymentMethod: "",
};

// ── Placement type catalogue ─────────────────────────────────────────────────

const PLACEMENT_TYPES = [
  {
    id:        "sponsored_landmark" as PlacementType,
    icon:      Landmark,
    label:     "Sponsored Landmark",
    tagline:   "Own a piece of the ISM world",
    desc:      "Name or brand a building, district, or landmark inside the ISM virtual universe. Maximum visibility — displayed on the map and in location details.",
    price:     499,
    period:    "/ month",
    highlight: true,
    color:     "from-yellow-500/20 to-amber-500/10 border-yellow-500/40",
    badge:     "Most Visible",
  },
  {
    id:        "ad_billboard" as PlacementType,
    icon:      Tv,
    label:     "Ad Billboard",
    tagline:   "Image or video at your location",
    desc:      "Display a full creative — image or short video — pinned to a specific location on the ISM map. Viewers see it when they explore that area.",
    price:     299,
    period:    "/ month",
    highlight: false,
    color:     "from-primary/20 to-primary/5 border-primary/30",
    badge:     "Most Popular",
  },
  {
    id:        "storefront" as PlacementType,
    icon:      Store,
    label:     "Storefront",
    tagline:   "Branded location on the map",
    desc:      "A persistent branded pin on the map with your logo, description, and click-through link. Great for businesses, brands, and creators.",
    price:     199,
    period:    "/ month",
    highlight: false,
    color:     "from-blue-500/20 to-blue-500/5 border-blue-500/30",
    badge:     null,
  },
  {
    id:        "event_pin" as PlacementType,
    icon:      MapPin,
    label:     "Event Pin",
    tagline:   "Map marker linked to your event",
    desc:      "Promote an upcoming event directly on the ISM world map. Shows date, ticket info, and RSVP link to every visitor who explores that area.",
    price:     79,
    period:    "/ event",
    highlight: false,
    color:     "from-green-500/20 to-green-500/5 border-green-500/30",
    badge:     null,
  },
] as const;

// ── Helpers ──────────────────────────────────────────────────────────────────

function calcTotal(type: PlacementType | "", days: number): number {
  const prices: Record<PlacementType, number> = {
    sponsored_landmark: 499, ad_billboard: 299, storefront: 199, event_pin: 79,
  };
  if (!type) return 0;
  const isMonthly = type !== "event_pin";
  const periods   = isMonthly ? Math.max(1, Math.round(days / 30)) : 1;
  return (prices[type] ?? 0) * periods;
}

function fmtPrice(cents: number): string {
  return `$${cents.toLocaleString()}`;
}

const STEPS = [
  "Placement Type",
  "Location",
  "Content & Details",
  "Contact Info",
  "Review & Pay",
];

const DURATION_OPTIONS = [
  { value: 30,  label: "1 month"   },
  { value: 60,  label: "2 months"  },
  { value: 90,  label: "3 months"  },
  { value: 180, label: "6 months"  },
  { value: 365, label: "12 months" },
];

// ── Main component ────────────────────────────────────────────────────────────

export default function MapPlacement() {
  const [search] = useLocation();
  const params   = new URLSearchParams(
    typeof search === "string" && search.startsWith("?") ? search : ""
  );
  const successPlacementId = params.get("placement_id");

  const [step,       setStep]       = useState(1);
  const [form,       setForm]       = useState<FormData>(EMPTY);
  const [spaces,     setSpaces]     = useState<Space[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const { toast }   = useToast();
  const { isConnected } = useWallet();

  // Load spaces for virtual location picker
  useEffect(() => {
    fetch(`${BASE}/api/spaces`)
      .then(r => r.json())
      .then((data: Space[]) => setSpaces(data))
      .catch(() => {});
  }, []);

  const set = (k: keyof FormData, v: any) =>
    setForm(prev => ({ ...prev, [k]: v }));

  const total = calcTotal(form.placementType, form.durationDays);

  // ── If returning from Stripe success ────────────────────────────────────────
  if (successPlacementId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center bg-card/60 border border-border rounded-2xl p-10"
        >
          <div className="w-16 h-16 bg-green-500/20 border border-green-500/40 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-green-400" />
          </div>
          <h1 className="text-2xl font-bold mb-3 uppercase tracking-wide">Placement Submitted</h1>
          <p className="text-muted-foreground mb-2">
            Your payment is confirmed. Placement <span className="text-primary font-mono">#{successPlacementId}</span> is now pending admin review.
          </p>
          <p className="text-xs text-muted-foreground mb-8">
            You'll be contacted at the email you provided once it's approved and live on the map.
          </p>
          <div className="flex flex-col gap-3">
            <a href={`${BASE}/world-map`}>
              <Button className="w-full bg-primary hover:bg-primary/80 font-bold uppercase tracking-widest">
                <Globe className="w-4 h-4 mr-2" />
                View World Map
              </Button>
            </a>
            <button
              onClick={() => { window.history.replaceState({}, "", `${BASE}/map-placement`); setStep(1); setForm(EMPTY); }}
              className="text-sm text-muted-foreground hover:text-white transition-colors"
            >
              Submit another placement
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // ── Step validation ───────────────────────────────────────────────────────
  function canProceed(): boolean {
    switch (step) {
      case 1: return !!form.placementType;
      case 2: {
        if (!form.locationType) return false;
        const needsVirtual   = form.locationType === "virtual" || form.locationType === "both";
        const needsRealWorld = form.locationType === "real_world" || form.locationType === "both";
        if (needsVirtual   && !form.virtualSpaceId)    return false;
        if (needsRealWorld && !form.realWorldAddress)  return false;
        return true;
      }
      case 3: return !!form.title;
      case 4: return !!form.contactName && !!form.contactEmail;
      case 5: return !!form.paymentMethod;
      default: return true;
    }
  }

  // ── Submit ─────────────────────────────────────────────────────────────────
  async function handleSubmit() {
    setSubmitting(true);
    try {
      const res = await fetch(`${BASE}/api/map-placements`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ ...form, durationDays: form.durationDays }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `Error ${res.status}`);
      }
      const data = await res.json() as { id: number; stripeUrl?: string };

      if (data.stripeUrl) {
        window.location.href = data.stripeUrl;
      } else {
        // Crypto path — show wallet toast and go to success screen
        toast({
          title:       "Request Submitted",
          description: "Complete the transaction in your wallet. Our team will confirm your payment and activate your placement.",
        });
        window.history.replaceState({}, "", `${BASE}/map-placement?placement_id=${data.id}`);
        window.location.reload();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Submission failed";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  const selectedType = PLACEMENT_TYPES.find(t => t.id === form.placementType);

  return (
    <div className="min-h-screen bg-background text-foreground pb-24">

      {/* Hero */}
      <div className="relative bg-gradient-to-b from-primary/10 via-background to-background border-b border-border px-6 py-12 text-center">
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono text-primary bg-primary/15 border border-primary/30 rounded-full px-3 py-1 mb-4">
          <MapPin className="w-3 h-3" />
          ISM Virtual World · Map Placement
        </span>
        <h1 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-3">
          Advertise on the Map
        </h1>
        <p className="text-muted-foreground max-w-xl mx-auto text-sm md:text-base">
          Place your event, brand, or advertisement directly inside the ISM virtual world map.
          Seen by every explorer across US digi-cities, metaverse spaces, and virtual venues.
        </p>
      </div>

      <div className="max-w-3xl mx-auto px-4 mt-10">

        {/* Step progress */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((label, i) => {
              const n       = i + 1;
              const done    = n < step;
              const current = n === step;
              return (
                <div key={n} className="flex flex-col items-center flex-1 relative">
                  {i < STEPS.length - 1 && (
                    <div className={`absolute top-4 left-1/2 w-full h-px ${done ? "bg-primary" : "bg-border"}`} />
                  )}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-mono font-bold z-10 border transition-all ${
                    done    ? "bg-primary border-primary text-primary-foreground" :
                    current ? "bg-primary/20 border-primary text-primary" :
                              "bg-background border-border text-muted-foreground"
                  }`}>
                    {done ? <CheckCircle2 className="w-4 h-4" /> : n}
                  </div>
                  <span className={`text-[10px] mt-1 font-mono uppercase tracking-wide hidden sm:block ${current ? "text-primary" : "text-muted-foreground"}`}>
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >

            {/* ── STEP 1: Placement Type ───────────────────────────────────── */}
            {step === 1 && (
              <div>
                <h2 className="text-xl font-bold uppercase tracking-widest mb-6">Choose Placement Type</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {PLACEMENT_TYPES.map(pt => {
                    const Icon     = pt.icon;
                    const selected = form.placementType === pt.id;
                    return (
                      <button
                        key={pt.id}
                        onClick={() => set("placementType", pt.id)}
                        className={`relative text-left rounded-2xl border p-5 bg-gradient-to-br transition-all duration-200 ${pt.color} ${
                          selected ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : "hover:brightness-110"
                        }`}
                      >
                        {pt.badge && (
                          <span className="absolute top-3 right-3 text-[9px] uppercase font-mono tracking-widest bg-primary/20 text-primary border border-primary/30 rounded-full px-2 py-0.5">
                            {pt.badge}
                          </span>
                        )}
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center">
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-bold text-sm leading-tight">{pt.label}</p>
                            <p className="text-xs text-muted-foreground">{pt.tagline}</p>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed mb-4">{pt.desc}</p>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl font-bold font-mono">${pt.price}</span>
                          <span className="text-xs text-muted-foreground">{pt.period}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── STEP 2: Location ─────────────────────────────────────────── */}
            {step === 2 && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold uppercase tracking-widest">Choose Location</h2>

                {/* Location type tabs */}
                <div>
                  <p className="text-xs uppercase tracking-widest font-mono text-muted-foreground mb-3">Where should this placement appear?</p>
                  <div className="flex gap-2 flex-wrap">
                    {([
                      { v: "virtual",    label: "Virtual Space",     icon: Globe       },
                      { v: "real_world", label: "Real-World Address", icon: Building2   },
                      { v: "both",       label: "Both",               icon: MapPin      },
                    ] as const).map(({ v, label, icon: Icon }) => (
                      <button
                        key={v}
                        onClick={() => set("locationType", v)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-mono transition-all ${
                          form.locationType === v
                            ? "bg-primary/20 border-primary text-primary"
                            : "border-border text-muted-foreground hover:border-white/30 hover:text-white"
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Virtual space picker */}
                {(form.locationType === "virtual" || form.locationType === "both") && (
                  <div className="space-y-4 bg-card/40 border border-border rounded-xl p-5">
                    <p className="text-xs uppercase tracking-widest font-mono text-primary">Virtual Location</p>
                    <div className="space-y-2">
                      <Label>ISM Space / City</Label>
                      <Select
                        value={form.virtualSpaceId ? String(form.virtualSpaceId) : ""}
                        onValueChange={v => {
                          const sp = spaces.find(s => s.id === Number(v));
                          set("virtualSpaceId", Number(v));
                          set("virtualSpaceName", sp?.name ?? "");
                        }}
                      >
                        <SelectTrigger className="bg-background">
                          <SelectValue placeholder="Select an ISM space…" />
                        </SelectTrigger>
                        <SelectContent>
                          {spaces.map(s => (
                            <SelectItem key={s.id} value={String(s.id)}>
                              {s.name}{s.cityName ? ` — ${s.cityName}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Specific spot within that space (optional)</Label>
                      <Input
                        placeholder="e.g. Near the main entrance, rooftop terrace, east wall…"
                        value={form.virtualLocationDetail}
                        onChange={e => set("virtualLocationDetail", e.target.value)}
                        className="bg-background"
                      />
                    </div>
                  </div>
                )}

                {/* Real-world address */}
                {(form.locationType === "real_world" || form.locationType === "both") && (
                  <div className="space-y-4 bg-card/40 border border-border rounded-xl p-5">
                    <p className="text-xs uppercase tracking-widest font-mono text-primary">Real-World Address</p>
                    <div className="space-y-2">
                      <Label>Street Address / Landmark</Label>
                      <Input
                        placeholder="1302 High St. or Navy Pier, Chicago"
                        value={form.realWorldAddress}
                        onChange={e => set("realWorldAddress", e.target.value)}
                        className="bg-background"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>City</Label>
                      <Input
                        placeholder="South Bend, IN"
                        value={form.realWorldCity}
                        onChange={e => set("realWorldCity", e.target.value)}
                        className="bg-background"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      We'll map this to the nearest ISM digi-city and link it in the virtual directory.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* ── STEP 3: Content & Details ────────────────────────────────── */}
            {step === 3 && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold uppercase tracking-widest">Content & Details</h2>

                <div className="space-y-2">
                  <Label htmlFor="title">Placement Title <span className="text-red-400">*</span></Label>
                  <Input
                    id="title"
                    placeholder={
                      form.placementType === "event_pin"   ? "e.g. Cypher Miami — Circle of Influence" :
                      form.placementType === "storefront"  ? "e.g. Caribbean Fusion Vegan — Miami" :
                      form.placementType === "ad_billboard"? "e.g. ISM ProdCo — Summer Campaign" :
                      "e.g. Emerald District, sponsored by ISM ProdCo"
                    }
                    value={form.title}
                    onChange={e => set("title", e.target.value)}
                    className="bg-background"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="What should viewers know when they click your placement? Include your offer, event details, or brand story."
                    value={form.description}
                    onChange={e => set("description", e.target.value)}
                    className="bg-background resize-none h-24"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5"><Image className="w-3.5 h-3.5" />Image URL</Label>
                    <Input
                      placeholder="https://… (JPG, PNG, GIF)"
                      value={form.imageUrl}
                      onChange={e => set("imageUrl", e.target.value)}
                      className="bg-background"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5"><Video className="w-3.5 h-3.5" />Video URL</Label>
                    <Input
                      placeholder="https://… (MP4, Dropbox raw=1)"
                      value={form.videoUrl}
                      onChange={e => set("videoUrl", e.target.value)}
                      className="bg-background"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" />Click-Through Link</Label>
                  <Input
                    placeholder="https://… (where viewers go when they click your placement)"
                    value={form.externalLinkUrl}
                    onChange={e => set("externalLinkUrl", e.target.value)}
                    className="bg-background"
                  />
                </div>

                {/* Duration — only for monthly types */}
                {form.placementType !== "event_pin" && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />Duration</Label>
                    <div className="flex gap-2 flex-wrap">
                      {DURATION_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => set("durationDays", opt.value)}
                          className={`px-4 py-2 rounded-full border text-sm font-mono transition-all ${
                            form.durationDays === opt.value
                              ? "bg-primary/20 border-primary text-primary"
                              : "border-border text-muted-foreground hover:border-white/30 hover:text-white"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Total: <span className="text-primary font-mono font-bold">{fmtPrice(total)}</span>
                    </p>
                  </div>
                )}

                <p className="text-xs text-muted-foreground bg-card/40 border border-border rounded-lg p-3">
                  <strong className="text-white">Media note:</strong> Provide externally hosted URLs for images and videos
                  (Dropbox <code className="text-primary">?raw=1</code>, Cloudinary, imgbb, etc.).
                  An ISM media team member will review all content before approval.
                </p>
              </div>
            )}

            {/* ── STEP 4: Contact Info ─────────────────────────────────────── */}
            {step === 4 && (
              <div className="space-y-5">
                <h2 className="text-xl font-bold uppercase tracking-widest">Contact Information</h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5" />Name <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      placeholder="Your full name"
                      value={form.contactName}
                      onChange={e => set("contactName", e.target.value)}
                      className="bg-background"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5">
                      <Briefcase className="w-3.5 h-3.5" />Company / Brand
                    </Label>
                    <Input
                      placeholder="Business or brand name"
                      value={form.contactCompany}
                      onChange={e => set("contactCompany", e.target.value)}
                      className="bg-background"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5" />Email <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={form.contactEmail}
                      onChange={e => set("contactEmail", e.target.value)}
                      className="bg-background"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5" />Phone
                    </Label>
                    <Input
                      type="tel"
                      placeholder="+1 (555) 000-0000"
                      value={form.contactPhone}
                      onChange={e => set("contactPhone", e.target.value)}
                      className="bg-background"
                    />
                  </div>
                </div>

                <p className="text-xs text-muted-foreground bg-card/40 border border-border rounded-lg p-3">
                  We'll reach out to this contact once your placement is reviewed and approved, and send a confirmation once it goes live on the map.
                </p>
              </div>
            )}

            {/* ── STEP 5: Review & Pay ─────────────────────────────────────── */}
            {step === 5 && selectedType && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold uppercase tracking-widest">Review & Pay</h2>

                {/* Summary card */}
                <div className="bg-card/50 border border-border rounded-2xl p-6 space-y-4">
                  <p className="text-xs uppercase tracking-widest font-mono text-muted-foreground">Order Summary</p>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center shrink-0">
                      <selectedType.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold">{selectedType.label}</p>
                      <p className="text-sm text-muted-foreground truncate">{form.title}</p>
                    </div>
                    <p className="font-mono font-bold text-lg shrink-0">{fmtPrice(total)}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm pt-2 border-t border-border">
                    {[
                      ["Location",  form.virtualSpaceName || form.realWorldCity || form.realWorldAddress || "—"],
                      ["Duration",  form.placementType === "event_pin" ? "Per event" :
                                    `${Math.max(1, Math.round(form.durationDays / 30))} month${Math.round(form.durationDays / 30) > 1 ? "s" : ""}`],
                      ["Contact",   `${form.contactName}${form.contactCompany ? ` · ${form.contactCompany}` : ""}`],
                      ["Email",     form.contactEmail],
                    ].map(([k, v]) => (
                      <div key={k}>
                        <p className="text-xs text-muted-foreground font-mono uppercase">{k}</p>
                        <p className="text-sm truncate">{v}</p>
                      </div>
                    ))}
                  </div>

                  {form.imageUrl && (
                    <div className="relative w-full h-32 rounded-xl overflow-hidden border border-border mt-2">
                      <img src={form.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <span className="font-mono text-sm uppercase tracking-widest text-muted-foreground">Total Due</span>
                    <span className="text-2xl font-mono font-bold text-primary">{fmtPrice(total)}</span>
                  </div>
                </div>

                {/* Payment method */}
                <div>
                  <p className="text-xs uppercase tracking-widest font-mono text-muted-foreground mb-3">Payment Method</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Stripe card */}
                    <button
                      onClick={() => set("paymentMethod", "stripe")}
                      className={`flex items-center gap-3 p-4 rounded-xl border text-left transition-all ${
                        form.paymentMethod === "stripe"
                          ? "bg-primary/20 border-primary ring-1 ring-primary"
                          : "border-border hover:border-white/30"
                      }`}
                    >
                      <CreditCard className="w-5 h-5 text-primary shrink-0" />
                      <div>
                        <p className="font-bold text-sm">Pay by Card</p>
                        <p className="text-xs text-muted-foreground">Secure checkout via Stripe</p>
                      </div>
                    </button>

                    {/* Crypto wallet */}
                    <button
                      onClick={() => set("paymentMethod", "crypto")}
                      className={`flex items-center gap-3 p-4 rounded-xl border text-left transition-all ${
                        form.paymentMethod === "crypto"
                          ? "bg-primary/20 border-primary ring-1 ring-primary"
                          : "border-border hover:border-white/30"
                      } ${!isConnected ? "opacity-60" : ""}`}
                    >
                      <Wallet className="w-5 h-5 text-primary shrink-0" />
                      <div>
                        <p className="font-bold text-sm">Pay with Wallet</p>
                        <p className="text-xs text-muted-foreground">
                          {isConnected ? "ISM / GLO / HALO — 10% discount" : "Connect your wallet first"}
                        </p>
                      </div>
                    </button>
                  </div>

                  {form.paymentMethod === "crypto" && !isConnected && (
                    <p className="mt-2 text-xs text-yellow-400 font-mono">
                      ⚠ Connect your wallet (top-right) before selecting crypto payment.
                    </p>
                  )}
                </div>

                <p className="text-xs text-muted-foreground">
                  By submitting you agree to ISM's placement terms. Card payments are held while your request
                  is reviewed — you'll receive a full refund if rejected.
                  Crypto payments are confirmed on-chain; our team will verify and activate your placement within 24–48h.
                </p>
              </div>
            )}

          </motion.div>
        </AnimatePresence>

        {/* Nav buttons */}
        <div className="mt-8 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => setStep(s => s - 1)}
            disabled={step === 1}
            className="flex items-center gap-2 text-muted-foreground"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>

          {step < 5 ? (
            <Button
              onClick={() => setStep(s => s + 1)}
              disabled={!canProceed()}
              className="bg-primary hover:bg-primary/80 font-bold uppercase tracking-widest flex items-center gap-2"
            >
              Continue
              <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed() || submitting || (form.paymentMethod === "crypto" && !isConnected)}
              className="bg-primary hover:bg-primary/80 font-bold uppercase tracking-widest flex items-center gap-2 min-w-[160px]"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" />Processing…</>
              ) : form.paymentMethod === "stripe" ? (
                <><CreditCard className="w-4 h-4" />Pay {fmtPrice(total)}</>
              ) : (
                <><Wallet className="w-4 h-4" />Submit Request</>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
